import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { ClientApprovalRequest } from "~/types/clientApproval";
import { Database } from "~/types/supabase";

type ContactAttempt = Database["public"]["Tables"]["contact_attempts"]["Row"];

interface ApproveInput {
  requestId: string;
  assignedSeller: string | null;
}

interface RejectInput {
  requestId: string;
  reason: string;
}

// Queue of AI-created client-creation requests (pending/approved/rejected).
export const useClientApprovals = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: requests = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["client-approvals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_approval_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ClientApprovalRequest[];
    },
  });

  const approve = useMutation({
    mutationFn: async ({ requestId, assignedSeller }: ApproveInput) => {
      const { data, error } = await supabase.rpc("approve_client_request", {
        p_request_id: requestId,
        p_assigned_seller: assignedSeller ?? undefined,
      });

      if (error) throw error;
      return data as string; // new client id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-approvals"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast({
        title: "Cliente aprobado",
        description: "El cliente fue creado a partir de la solicitud.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo aprobar la solicitud.",
        variant: "destructive",
      });
    },
  });

  const reject = useMutation({
    mutationFn: async ({ requestId, reason }: RejectInput) => {
      const { error } = await supabase.rpc("reject_client_request", {
        p_request_id: requestId,
        p_reason: reason,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-approvals"] });
      toast({
        title: "Solicitud rechazada",
        description: "La solicitud fue marcada como rechazada.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo rechazar la solicitud.",
        variant: "destructive",
      });
    },
  });

  return {
    requests,
    isLoading,
    error,
    approve: approve.mutateAsync,
    reject: reject.mutateAsync,
    isApproving: approve.isPending,
    isRejecting: reject.isPending,
  };
};

interface LeadCorrelation {
  email?: string | null;
  phone?: string | null;
  company?: string | null;
}

// Correlate a lead to its cotizaciones (contact_attempts) by email / phone / company.
// There is no explicit FK, so we match on the contact fields the agent also stored on
// the cotización, then de-duplicate by id.
export const useLeadCotizaciones = (lead: LeadCorrelation | null) => {
  const email = lead?.email?.trim() || null;
  const phone = lead?.phone?.trim() || null;
  const company = lead?.company?.trim() || null;
  const enabled = Boolean(email || phone || company);

  const { data: cotizaciones = [], isLoading } = useQuery({
    queryKey: ["lead-cotizaciones", email, phone, company],
    enabled,
    queryFn: async () => {
      // Run one filtered query per available key (values may contain commas that
      // would break a combined PostgREST .or() string), then merge by id.
      const queries: Promise<{ data: ContactAttempt[] | null }>[] = [];

      if (email) {
        queries.push(
          supabase
            .from("contact_attempts")
            .select("*")
            .ilike("email", email)
            .then(({ data }) => ({ data: data as ContactAttempt[] | null }))
        );
      }
      if (phone) {
        queries.push(
          supabase
            .from("contact_attempts")
            .select("*")
            .eq("phone", phone)
            .then(({ data }) => ({ data: data as ContactAttempt[] | null }))
        );
      }
      if (company) {
        queries.push(
          supabase
            .from("contact_attempts")
            .select("*")
            .ilike("company", company)
            .then(({ data }) => ({ data: data as ContactAttempt[] | null }))
        );
      }

      const results = await Promise.all(queries);
      const byId = new Map<string, ContactAttempt>();
      for (const { data } of results) {
        for (const row of data ?? []) byId.set(row.id, row);
      }
      return Array.from(byId.values()).sort((a, b) =>
        (b.created_at ?? "").localeCompare(a.created_at ?? "")
      );
    },
  });

  return { cotizaciones, isLoading };
};
